import type {
  RunDetailResponse,
  RunPage,
  RunSummary,
} from "contracts/api/crawl/run-read.responses";
import { CLIENT_FINDING_FIELD } from "contracts/enums/client-finding";
import type { CrawlFailureReason } from "contracts/enums/core";
import {
  CRAWL_FAILURE_REASON,
  CRAWL_FRONTIER_STATUS,
  CRAWL_RUN_STATUS,
  isActiveCrawlRunStatus,
} from "contracts/enums/core";
import { PAGE_ANALYSIS_STATUS } from "contracts/enums/page-analysis";
import { YEET_ATTEMPT_STATUS, YEET_FETCH_STATUS } from "contracts/enums/yeet";
import { clientFacingFindingText } from "contracts/report/boundaries/client-safe-finding.parser";
import { CRAWL_FAILURE_REASON_LABELS } from "../../enums/run-failures";
import { RUN_FAILURE_KIND } from "../../enums/runs";
import { pluralize } from "../../ui/format";
import { runStatusLabel } from "../scan/clientLanguage";
import { clientDisplayText } from "../scan/evidencePresentation";
import { pageFailurePresentation } from "./runFailurePresentation";

function pageStatusLabel(status: RunPage["status"]): string {
  if (status === CRAWL_FRONTIER_STATUS.IN_FLIGHT) return "In progress";
  if (status === CRAWL_FRONTIER_STATUS.SCRAPED) return "Completed";
  if (status === CRAWL_FRONTIER_STATUS.FAILED) return "Not completed";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Where a page currently sits, distinguishing a fetch wait from a sandbox wait. */
export function runPageStageLabel(page: RunPage): string {
  if (page.status !== CRAWL_FRONTIER_STATUS.IN_FLIGHT)
    return pageStatusLabel(page.status);
  if (
    page.fetchStatus === null ||
    page.fetchStatus === YEET_FETCH_STATUS.PENDING
  ) {
    return "Fetching";
  }
  if (page.attemptStatus === null) return "Waiting for analysis";
  return "Analysing";
}

type FailureGroup = {
  reason: CrawlFailureReason;
  label: string;
  pages: RunPage[];
};

/**
 * Failed pages grouped by reason, largest group first. A run that fails 30
 * pages for one reason is one fact, not thirty — the per-page list stays
 * available underneath each group.
 */
export function groupFailuresByReason(pages: RunPage[]): FailureGroup[] {
  const groups = new Map<CrawlFailureReason, RunPage[]>();
  for (const page of pages) {
    const reason = page.failureReason;
    if (page.status !== CRAWL_FRONTIER_STATUS.FAILED || !reason) continue;
    const existing = groups.get(reason);
    if (existing) existing.push(page);
    else groups.set(reason, [page]);
  }
  return [...groups.entries()]
    .map(([reason, grouped]) => ({
      reason,
      label: CRAWL_FAILURE_REASON_LABELS[reason],
      pages: grouped,
    }))
    .sort((a, b) => b.pages.length - a.pages.length);
}

export function attemptStatusLabel(status: RunPage["attemptStatus"]): string {
  if (status === YEET_ATTEMPT_STATUS.RUNNING) return "In progress";
  if (status === PAGE_ANALYSIS_STATUS.COMPLETED) return "Completed";
  if (status === "failed") return "Not completed";
  return "Not started";
}

export function runPartialCoverageSummary(
  run: RunSummary,
  loadedPages: RunPage[],
): string | null {
  const incomplete = run.pageCounts.failed;
  const skipped = run.pageCounts.skipped;
  if (
    run.status !== CRAWL_RUN_STATUS.COMPLETED ||
    (incomplete === 0 && skipped === 0)
  ) {
    return null;
  }
  const failedPages = loadedPages.filter(
    (page) => page.status === CRAWL_FRONTIER_STATUS.FAILED,
  );
  const kinds = new Set(
    failedPages.flatMap((page) => {
      const presentation = pageFailurePresentation(page);
      return presentation ? [presentation.kind] : [];
    }),
  );
  const detail =
    incomplete === 0
      ? "Skipped pages were not assessed in this run."
      : kinds.size === 1 && kinds.has(RUN_FAILURE_KIND.ASSESSMENT)
        ? "The affected pages were captured, but Faro's assessment process did not finish."
        : kinds.size === 1 && kinds.has(RUN_FAILURE_KIND.CAPTURE)
          ? "Faro could not capture or prepare the affected pages for assessment."
          : "Open each page's Technical detail to review the recorded cause.";
  const coverageParts = [
    `${run.pageCounts.scraped} ${pluralize(run.pageCounts.scraped, "page")} completed`,
    ...(incomplete > 0
      ? [
          `${incomplete} ${pluralize(incomplete, "page was", "pages were")} not completed`,
        ]
      : []),
    ...(skipped > 0
      ? [`${skipped} ${pluralize(skipped, "page was", "pages were")} skipped`]
      : []),
  ];
  return `This run completed with partial coverage: ${coverageParts.join("; ")}. ${detail}`;
}

export function runFindingSummary(
  finding: RunDetailResponse["findings"][number],
): string {
  return (
    clientFacingFindingText(
      clientDisplayText(finding.detail),
      CLIENT_FINDING_FIELD.SUMMARY,
    ) ??
    "The scan recorded a page-level finding. Review Technical detail and the stored page evidence before acting."
  );
}

export function runFindingAction(
  finding: RunDetailResponse["findings"][number],
): string {
  return (
    clientFacingFindingText(
      finding.recommendedAction
        ? clientDisplayText(finding.recommendedAction)
        : null,
      CLIENT_FINDING_FIELD.ACTION,
    ) ??
    "Review the page evidence, confirm the observation and define the appropriate site change."
  );
}

/**
 * A user-initiated cancel is not a critical failure, so it does not get the
 * critical badge under "What went wrong".
 */
export function failureSeverityClass(reason: CrawlFailureReason): string {
  return reason === CRAWL_FAILURE_REASON.ABORTED
    ? "badge-status st-done"
    : "badge-sev sev-critical";
}

export function runSummaryStatusLabel(run: RunSummary): string {
  return runStatusLabel(
    run.status,
    run.status === CRAWL_RUN_STATUS.COMPLETED &&
      (run.pageCounts.failed > 0 || run.pageCounts.skipped > 0),
  );
}

export function runStatusClass(run: RunSummary): string {
  if (isActiveCrawlRunStatus(run.status)) return "badge-status st-in_progress";
  if (run.status === CRAWL_RUN_STATUS.FAILED) return "badge-sev sev-critical";
  return "badge-status st-done";
}

export function runDuration(run: RunSummary): string {
  if (!run.finishedAt) return "—";
  const seconds = Math.round(
    (run.finishedAt.getTime() - run.startedAt.getTime()) / 1000,
  );
  if (seconds < 90) return `${seconds}s`;
  return `${Math.round(seconds / 60)}m`;
}

export function runFindingTitle(
  finding: RunDetailResponse["findings"][number],
): string {
  return (
    clientFacingFindingText(
      clientDisplayText(finding.title),
      CLIENT_FINDING_FIELD.TITLE,
    ) ?? "Page-level issue recorded"
  );
}
